<script setup lang="ts">
  import { computed } from "vue";
  import { Separator } from "@/components/ui/separator";
  import type {
    Histo,
    HistoLearningsCost,
    HistoLearningsIssue,
    HistoLearningsComments,
  } from "@/components/Admin/Histo/HistoDataTable/types";

  const props = defineProps<{
    histo: Histo;
  }>();

  const learningsIssues = computed<HistoLearningsIssue[]>(() => {
    try {
      return JSON.parse(props.histo.learnings_issues || "[]");
    } catch {
      return [];
    }
  });

  const learningsCosts = computed<HistoLearningsCost[]>(() => {
    try {
      return JSON.parse(props.histo.learnings_costs || "[]");
    } catch {
      return [];
    }
  });

  const learningsComments = computed<HistoLearningsComments | null>(() => {
    try {
      return JSON.parse(props.histo.learnings_comments || "null");
    } catch {
      return null;
    }
  });
</script>

<template>
  <div class="space-y-6">
    <section>
      <h3 class="heading-section mb-3">Performance issues</h3>
      <template v-if="learningsIssues.length">
        <ul class="space-y-3 text-sm">
          <li
            v-for="(issue, i) in learningsIssues"
            :key="`issue-${i}`"
            class="border-l-2 border-status-warning pl-3 py-1">
            <span class="font-medium text-foreground">{{ issue.issue_type }}</span>
            <p
              v-if="issue.issue_comment"
              class="text-foreground/80 mt-0.5">
              {{ issue.issue_comment }}
            </p>
          </li>
        </ul>
      </template>
      <p
        v-else
        class="text-sm text-foreground/50 italic">
        No performance issues reported
      </p>
    </section>

    <Separator />

    <section>
      <h3 class="heading-section mb-3">External costs</h3>
      <template v-if="learningsCosts.length">
        <ul class="space-y-3 text-sm">
          <li
            v-for="(cost, i) in learningsCosts"
            :key="`cost-${i}`"
            class="border-l-2 border-status-info pl-3 py-1">
            <div class="flex items-center gap-2">
              <span class="font-medium text-foreground">{{ cost.cost_type }}</span>
              <span class="text-foreground/70">{{ cost.cost }}€</span>
            </div>
            <p
              v-if="cost.cost_comment"
              class="text-foreground/80 mt-0.5">
              {{ cost.cost_comment }}
            </p>
          </li>
        </ul>
      </template>
      <p
        v-else
        class="text-sm text-foreground/50 italic">
        No external costs
      </p>
    </section>

    <Separator />

    <section>
      <h3 class="heading-section mb-3">CSM comment</h3>
      <p class="text-sm text-foreground/80 leading-relaxed">
        {{ learningsComments?.csm_comment || "No comment" }}
      </p>
    </section>

    <Separator />

    <section>
      <h3 class="heading-section mb-3">TM comment</h3>
      <p class="text-sm text-foreground/80 leading-relaxed">
        {{ learningsComments?.tm_comment || "No comment" }}
      </p>
    </section>
  </div>
</template>
